import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import DataGridCustomToolbar from "components/DataGridCustomToolBar";
import { useProductEmissionsGrid } from "./hooks/useProductEmissionsGrid";

function ProductEmissionsGrid({ handleEditOpen }) {
  const {
    backgroundAlt,
    primaryLight,
    page,
    setPage,
    limit,
    setLimit,
    search,
    setSearch,
    sort,
    setSort,
    searchInput,
    setSearchInput,
    rows,
    columns,
    totalProducts,
    ingredientColumns,
    getPreferredEmission,
    // new:
    selectedProducts,
    handleSelectionChange,
    selectedColumns,
    totalCarbonOutput,
    formatNumber,
  } = useProductEmissionsGrid({ handleEditOpen });

  const sortModel = useMemo(() => {
    if (!sort) return [];
    const m = sort.match(/^(.*?):(asc|desc)$/); // New with colon
    if (!m) return [];
    return [{ field: m[1], sort: m[2] }];
  }, [sort]);

  return (
    <Box
      mt="40px"
      width="100%"
      sx={{
        "& .MuiDataGrid-columnHeaders": { backgroundColor: backgroundAlt },
        "& .MuiDataGrid-virtualScroller": { backgroundColor: backgroundAlt },
        "& .MuiDataGrid-footerContainer": { backgroundColor: primaryLight },
      }}
    >
      <DataGridPro
        rows={rows}
        columns={columns}
        getRowId={(r) => r.id}
        // server pagination
        pagination
        paginationMode="server"
        pageSize={limit}
        rowCount={totalProducts}
        paginationModel={{ page, pageSize: limit }}
        onPaginationModelChange={({ page, pageSize }) => {
          setPage(page); // 0-based
          setLimit(pageSize);
        }}
        pageSizeOptions={[5, 10, 25, 50, 100]}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={(model) => {
          const m = model[0];
          if (m) {
            // build exactly the same string your hook expects
            setSort(`${m.field}:${m.sort}`);
          } else {
            setSort("");
          }
        }}
        checkboxSelection
        onRowSelectionModelChange={handleSelectionChange}
        rowSelectionModel={selectedProducts.map((p) => p.id)}
        components={{ Toolbar: DataGridCustomToolbar }}
        componentsProps={{
          toolbar: { searchInput, setSearchInput, search, setSearch },
        }}
        disableSelectionOnClick
        getDetailPanelContent={({ row }) => {
          const detailRows = (row.ingredients || []).map((ing, idx) => {
            const ems = ing?.emissionRecordId?.emissions; // <- guard
            const base = getPreferredEmission(ems);
            const pct = Number(ing?.percent) || 0;

            return {
              id: `${row.id}-${ing?._id || idx}`,
              ingredientName:
                ing?.emissionRecordId?.source_product_name ?? "(Unnamed)",
              percent: pct,
              totalCo2: base,
              co2PerKg: (base * pct) / 100,
              dataQualityScore:
                Number(ing?.emissionRecordId?.data_quality_score) || 0,
            };
          });

          return (
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Ingredient summary
              </Typography>
              <DataGridPro
                rows={detailRows}
                columns={ingredientColumns}
                hideFooter
                disableSelectionOnClick
              />
            </Box>
          );
        }}
        getDetailPanelHeight={() => "auto"}
        // *** v6 toolbar API ***
        slots={{ toolbar: DataGridCustomToolbar }}
        slotProps={{
          toolbar: {
            searchInput,
            setSearchInput,
            setSearch,
            showSearchBar: true,
            showColumnsButton: true,
          },
        }}
      />

      {selectedProducts.length > 0 && (
        <Box mt={4} p={2} bgcolor={backgroundAlt} borderRadius="8px">
          <Typography variant="h6" fontWeight="bold" mb={1}>
            Selected Products for Comparison
          </Typography>

          <DataGridPro
            rows={selectedProducts}
            columns={selectedColumns}
            pageSize={5}
            disableSelectionOnClick
          />

          <Box mt={2} textAlign="right">
            <Typography variant="h6">
              Total Selected Products: {selectedProducts.length}
            </Typography>
            <Typography variant="body1" mt={1}>
              Total CO₂e:{" "}
              <Typography fontWeight={"bold"} component="span">
                {formatNumber(totalCarbonOutput / 1000, 1)} tonnes
              </Typography>{" "}
              / {formatNumber(totalCarbonOutput, 2)} kg
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default ProductEmissionsGrid;
